import { useEffect, useState } from "react";
import { apiGet } from "../../utils/apiClient";
import { getPlayersUrl } from "../../config/api";
import { League } from "../../context/LeagueContext";
import { Player } from "../../types/models";

/** How many players the server returns per search. */
export const PLAYER_SEARCH_LIMIT = 20;

interface LeaguePlayerSearch {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  players: Player[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Debounced, abortable league-wide player search. A substitute can be any
 * player in the league, so the search runs on the server rather than
 * filtering one page of players in the browser.
 */
export function useLeaguePlayerSearch(
  league: League | null,
  token: string,
): LeaguePlayerSearch {
  const [searchTerm, setSearchTerm] = useState("");
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!league) {
      return;
    }

    const abortController = new AbortController();
    const trimmedQuery = searchTerm.trim();
    // Load immediately when empty; debounce while the user is typing.
    const delay = trimmedQuery === "" ? 0 : 250;

    const timeoutId = setTimeout(() => {
      setIsLoading(true);

      const baseUrl = getPlayersUrl(league.id);
      const sort = `limit=${PLAYER_SEARCH_LIMIT}&sort=last_name,first_name`;
      const searchUrl = trimmedQuery
        ? `${baseUrl}?q=${encodeURIComponent(trimmedQuery)}&${sort}`
        : `${baseUrl}?${sort}`;

      apiGet(searchUrl, {
        token,
        ...(league.abbr ? { leagueAbbr: league.abbr } : {}),
      })
        .then((response) => {
          if (abortController.signal.aborted) {
            return;
          }
          setPlayers(Array.isArray(response) ? response : response.data || []);
          setError(null);
        })
        .catch((caught) => {
          if (!abortController.signal.aborted) {
            setError(caught.message || "Failed to load players");
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
          }
        });
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [league, searchTerm, token]);

  return { searchTerm, setSearchTerm, players, isLoading, error };
}
